/**
 * lib/teacherAnalysis.ts
 * ------------------------------------------------------------------
 * Per-teacher / per-teaching-group analysis. For a class+term it
 * groups students by WHO teaches them each subject
 * (student.subjectTeachers[subjectId]) and, using the SAME grading
 * engine the report uses (deriveMark), counts how many students in
 * each group landed in each grade band, plus totals and pass rate.
 *
 * This is the LKC HOD/management view: "for Mr X's ICT Group L —
 * N students, this many A*, A, B … and X% pass".
 *
 * Pure functions only (no Firestore / React) so the numbers are
 * unit-testable and provably match the rest of the app. Depends
 * entirely on the S1 subjectTeachers link being populated; with it
 * empty everything collapses into an "Unassigned" group, which the
 * UI surfaces honestly rather than hiding.
 * ------------------------------------------------------------------
 */
import {
  deriveMark,
  schemeForSubject,
  GRADE_ORDER_ALL,
  gradeFor,
  isBlank,
} from '@/lib/grading';
import { departmentOfSubject } from '@/lib/departments';
import type {
  Student,
  Subject,
  Teacher,
  SubjectMarkEntry,
  MaybeMark,
} from '@/types';

// Union of both tiers (A*..G,U) so Core-only F/G get real buckets/columns.
export const GRADE_ORDER = GRADE_ORDER_ALL;

export interface TeacherGroupStat {
  teacherId: string;
  teacherName: string;
  subjectId: string;
  subjectName: string;
  subjectCategory: string; // subject-family taxonomy (display only)
  subjectDepartment: string; // canonical academic department (lib/departments)
  total: number; // students taught in this group
  graded: number; // of those, how many have a usable mark
  gradeCounts: Record<string, number>; // grade -> count
  passCount: number;
  passRate: number | null; // % of graded, null if none graded
  average: number | null;
}

export type GroupBy = 'none' | 'department' | 'teacher' | 'subject';

/** Filter stats to a chosen set of teacher ids (empty = all). */
export function filterByTeachers(
  stats: TeacherGroupStat[],
  teacherIds: string[],
): TeacherGroupStat[] {
  if (!teacherIds.length) return stats;
  const set = new Set(teacherIds);
  return stats.filter((s) => set.has(s.teacherId));
}

/**
 * Bucket stats for display. 'department' => by subjectDepartment (the
 * canonical academic department from lib/departments, NOT the legacy
 * subjectCategory), 'subject' => by subjectName, 'teacher' => by
 * teacherName, 'none' => one bucket. Returns ordered [label, rows][]
 * so the UI/PDF can render sections.
 */
export function groupStats(
  stats: TeacherGroupStat[],
  by: GroupBy,
): { label: string; rows: TeacherGroupStat[] }[] {
  if (by === 'none')
    return [{ label: 'All teaching groups', rows: stats }];
  const m = new Map<string, TeacherGroupStat[]>();
  for (const s of stats) {
    const key =
      by === 'department'
        ? s.subjectDepartment || 'Unassigned'
        : by === 'subject'
          ? s.subjectName
          : s.teacherName;
    (m.get(key) ?? m.set(key, []).get(key)!).push(s);
  }
  return [...m.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([label, rows]) => ({
      label:
        by === 'department'
          ? label.charAt(0).toUpperCase() + label.slice(1)
          : label,
      rows,
    }));
}

export interface AnalysisInput {
  term: string;
  passMark: number;
  /** Force the analysis basis. When set, overrides the per-group auto
   *  exam-phase detection: 'exam' analyses the exam mark for every group,
   *  'ca' the CA/term mark. Undefined = auto (CA until a group's exam starts). */
  basisOverride?: 'ca' | 'exam';
  students: Student[]; // already filtered to the class
  subjects: Subject[]; // subjects in play
  teachers: Teacher[];
  // markEntry per subjectId: the loaded reportMarks doc's entries
  // { subjectId: { studentId: SubjectMarkEntry } }
  marksBySubject: Record<
    string,
    Record<string, SubjectMarkEntry>
  >;
}

/**
 * One row per (teacher, subject) teaching group. Students with no
 * recorded subject teacher for a subject fall into a synthetic
 * "__unassigned__" teacher so the gap is visible, never hidden.
 */
export function buildTeacherGroupStats(
  input: AnalysisInput,
): TeacherGroupStat[] {
  const { term, passMark, students, subjects, teachers } = input;
  const tName = (id: string) =>
    id === '__unassigned__'
      ? '⚠ Unassigned (no subject teacher set)'
      : (teachers.find((t) => t.id === id)?.displayName ??
        'Unknown teacher');
  const sName = (id: string) =>
    subjects.find((s) => s.id === id)?.name ?? id;
  const sCat = (id: string) =>
    subjects.find((s) => s.id === id)?.category || 'uncategorised';

  // Credit the teacher STAMPED on the mark (frozen at save); fall back to the
  // live subjectTeachers link for un-stamped/absent entries, so prior-term
  // stats stay with the teacher who actually taught them even after a mid-year
  // Teacher Swap. Same resolution in the exam-phase pre-pass and the main loop.
  const teacherIdFor = (
    e: SubjectMarkEntry | undefined,
    stu: Student,
    subjId: string,
  ) => (e?.teacherId ?? stu.subjectTeachers?.[subjId]) || '__unassigned__';

  // Pre-pass: is each (teacher, subject) group in the EXAM phase? A group
  // enters the exam phase as soon as ANY of its students has a non-blank exam;
  // until then it's the CA phase. In the CA phase we grade on the CA/term mark
  // (not the exam-derived gradeSource, which is null while exams are blank),
  // so in-progress Term 2/3 analysis isn't stuck at 0/N. Term 1 never uses
  // exams -> every group stays in the CA phase, identical to before.
  const examPhaseStarted = new Map<string, boolean>(); // `${teacherId}__${subjectId}`
  for (const subj of subjects) {
    const entries = input.marksBySubject[subj.id] ?? {};
    for (const stu of students) {
      if (!stu.subjects?.includes(subj.id)) continue;
      const e = entries[stu.id];
      const key = `${teacherIdFor(e, stu, subj.id)}__${subj.id}`;
      if (!examPhaseStarted.has(key)) examPhaseStarted.set(key, false);
      if (e && !isBlank(e.exam as MaybeMark)) examPhaseStarted.set(key, true);
    }
  }

  // key = `${teacherId}__${subjectId}`
  const groups = new Map<string, TeacherGroupStat>();

  for (const subj of subjects) {
    const entries = input.marksBySubject[subj.id] ?? {};
    const scheme = schemeForSubject(subj);
    for (const stu of students) {
      if (!stu.subjects?.includes(subj.id)) continue;
      const e = entries[stu.id];
      const teacherId = teacherIdFor(e, stu, subj.id);
      const key = `${teacherId}__${subj.id}`;
      let g = groups.get(key);
      if (!g) {
        g = {
          teacherId,
          teacherName: tName(teacherId),
          subjectId: subj.id,
          subjectName: sName(subj.id),
          subjectCategory: sCat(subj.id),
          subjectDepartment: departmentOfSubject(subj, teachers),
          total: 0,
          graded: 0,
          gradeCounts: Object.fromEntries(
            GRADE_ORDER.map((gr) => [gr, 0]),
          ),
          passCount: 0,
          passRate: null,
          average: null,
        };
        groups.set(key, g);
      }
      g.total += 1;

      if (!e) continue;
      const d = deriveMark(term, e.ca, e.exam, scheme);
      // Phase-aware mark source, chosen PER GROUP: CA phase -> the CA/term
      // mark; exam phase -> the exam-derived gradeSource (blank exam still
      // -> null -> skipped, correctly flagging "needs exam"). graded,
      // average, passCount/passRate AND gradeCounts ALL derive from this one
      // chosen mark + its matching grade — never mixed (the can't-half-fix
      // rule): count CA toward graded => average/bucket the CA value too.
      const examPhase = input.basisOverride
        ? input.basisOverride === 'exam'
        : (examPhaseStarted.get(key) ?? false);
      const mark: MaybeMark | null = examPhase ? d.gradeSource : d.termMark;
      // Bucket by the scheme-correct report grade (Core caps at C), matching
      // the chosen mark: exam phase reuses d.reportGrade; CA phase grades the
      // term mark on the same scheme.
      const gr = examPhase ? d.reportGrade : gradeFor(d.termMark, scheme);
      if (mark === null || mark === undefined || mark === 'ABS')
        continue;
      const num = Number(mark);
      if (!Number.isFinite(num)) continue;
      g.graded += 1;
      if (gr && gr in g.gradeCounts) g.gradeCounts[gr] += 1;
      if (num >= passMark) g.passCount += 1;
      g.average = (g.average ?? 0) + num;
    }
  }

  const out = [...groups.values()].map((g) => ({
    ...g,
    passRate:
      g.graded > 0
        ? Math.round((g.passCount / g.graded) * 100)
        : null,
    average:
      g.graded > 0 && g.average !== null
        ? Math.round(g.average / g.graded)
        : null,
  }));

  out.sort(
    (a, b) =>
      a.subjectName.localeCompare(b.subjectName) ||
      a.teacherName.localeCompare(b.teacherName),
  );
  return out;
}

/** Flat rows for CSV/xlsx: one line per teaching group. */
export function statsToTable(
  stats: TeacherGroupStat[],
): (string | number)[][] {
  const header = [
    'Subject',
    'Teacher',
    'Students taught',
    'Graded',
    ...GRADE_ORDER,
    'Pass count',
    'Pass rate %',
    'Average %',
  ];
  const rows = stats.map((s) => [
    s.subjectName,
    s.teacherName,
    s.total,
    s.graded,
    ...GRADE_ORDER.map((g) => s.gradeCounts[g] ?? 0),
    s.passCount,
    s.passRate ?? '',
    s.average ?? '',
  ]);
  return [header, ...rows];
}
